// Generic confirmation dialog replacing window.confirm — in-app, theme-consistent,
// keyboard-accessible (Escape to cancel, Enter to confirm). Shows a loading state
// during the callback so the user always knows something is happening.
import { useEffect, useRef, type ReactNode } from "react";
import { Dialog } from "@grackle/ui";
import { Button } from "./ui";

interface Props {
  open: boolean;
  title: string;
  message: string | ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  danger,
  loading = false,
  onClose,
  onConfirm,
}: Props) {

  // Callers pass inline closures for these; routing the keydown handler through refs keeps
  // the effect below keyed on `open` alone, so a parent re-render doesn't tear down the
  // listener or re-steal focus from whatever the user is typing in (e.g. a password field
  // rendered inside `message`).
  const onCloseRef = useRef(onClose);
  const onConfirmRef = useRef(onConfirm);
  const loadingRef = useRef(loading);
  onCloseRef.current = onClose;
  onConfirmRef.current = onConfirm;
  loadingRef.current = loading;

  // Focus the confirm button and set up keyboard handling when the dialog opens
  useEffect(() => {
    if (!open) return;
    document.querySelector<HTMLButtonElement>("[data-confirm-action]")?.focus();

    function onKeydown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key === "Enter") {
        // A focused button already activates on Enter — handling it here too would
        // double-fire (worst case: Enter on Cancel both cancels and confirms).
        if (e.target instanceof HTMLButtonElement) return;
        if (loadingRef.current) return;
        e.preventDefault();
        onConfirmRef.current();
      }
    }
    document.addEventListener("keydown", onKeydown);
    return () => document.removeEventListener("keydown", onKeydown);
  }, [open]);

  if (!open) return null;

  return (
    <Dialog
      role="alertdialog"
      title={title}
      width={420}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-confirm-action
            variant={danger ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={loading}
            loading={loading}
          >
            {loading ? "Processing…" : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="dialog-message">{message}</div>
    </Dialog>
  );
}
